import React from 'react'
import {useTranslation} from 'react-i18next'
import {observer} from 'mobx-react'
import {List} from 'antd'
import classNames from 'classnames'
import {LoadingState} from '@/types'
import {useScreenType} from '@/hooks'
import {Card} from '@/components/dumb/Card'
import {DATE_TIME_FORMAT, TerminalType} from '@/constants'
import {TransactionSummaryListItem} from './components'
import {TransactionListProps} from './props'
import styles from './styles.scss'
import {PAYMENT_SUMMARY} from '@/constants/playwright-ids'

export const TransactionSummaryList: React.FC<TransactionListProps> = observer(
    ({summaryByStatus, loadingState, prevDates, type, currency}) => {
        const {t: tEnums} = useTranslation('enums')
        const {isMobile, isTablet, isLaptop} = useScreenType()

        const getListPropertiesByType = () => {
            switch (type) {
                case TerminalType.ECOM:
                    return {
                        gutter: 0,
                        xs: 1,
                        sm: 2,
                        md: 2,
                        lg: 4,
                        xl: 4,
                        xxl: 4
                    }
                case TerminalType.POS:
                    return {
                        gutter: 0,
                        xs: 1,
                        sm: 2,
                        md: 2,
                        lg: 2,
                        xl: 2,
                        xxl: 2
                    }
                case TerminalType.SETTLEMENTS:
                    return {
                        gutter: 0,
                        xs: 1,
                        sm: 2,
                        md: 2,
                        lg: 5,
                        xl: 5,
                        xxl: 5
                    }
                case TerminalType.ALL:
                    return {
                        gutter: 0,
                        xs: 1,
                        sm: 3,
                        md: 3,
                        lg: 3,
                        xl: 3,
                        xxl: 3
                    }
                default:
                    return {
                        gutter: 0,
                        xs: 1,
                        sm: 2,
                        md: 2,
                        lg: 2,
                        xl: 2,
                        xxl: 2
                    }
            }
        }

        const paymentStatusMap: Record<TerminalType, Record<string, string>> = {
            [TerminalType.ECOM]: tEnums('onlinePaymentStatus', {returnObjects: true}) as Record<
                string,
                string
            >,
            [TerminalType.POS]: tEnums('posPaymentStateStatus', {returnObjects: true}) as Record<
                string,
                string
            >,
            [TerminalType.SETTLEMENTS]: tEnums('settlementsStatus', {
                returnObjects: true
            }) as Record<string, string>,
            [TerminalType.ALL]: tEnums('paymentsStatus', {
                returnObjects: true
            }) as Record<string, string>
        }

        return (
            <Card
                loading={loadingState === LoadingState.LOADING}
                className={classNames(
                    {[styles.posCard]: type === TerminalType.POS},
                    {[styles.posCardLaptop]: type === TerminalType.POS && (isLaptop || isTablet)},
                    {[styles.posCardMobile]: type === TerminalType.POS && isMobile},
                    {[styles.fullWidthPosCard]: type === TerminalType.ALL || type === TerminalType.SETTLEMENTS}
                )}
                data-test-id={PAYMENT_SUMMARY}
            >
                <List
                    grid={getListPropertiesByType()}
                    itemLayout={'horizontal'}
                    dataSource={summaryByStatus}
                    renderItem={(item, index) => (
                        <TransactionSummaryListItem
                            period={`${prevDates.from.format(DATE_TIME_FORMAT)} - ${prevDates.to.format(DATE_TIME_FORMAT)}`}
                            item={item}
                            statusLabel={paymentStatusMap[type]?.[item.status]}
                            hasDivider={summaryByStatus.length - 1 !== index}
                            currency={currency}
                            type={type}
                        />
                    )}
                />
            </Card>
        )
    }
)
